import { Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { Link, useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { browserPage, isChunkLoadError } from '@/app/staleChunk';
import { ApiError, ERROR_CODES } from '@/shared/api/http';
import { useOnline } from '@/shared/hooks/useOnline';

/**
 * The last line of the router (13 §2.1): when the data a route needs fails to load, the console
 * shows this instead of going blank. A rate limit or a network blip is recoverable, so the first
 * button retries the same route; the correlation id travels in the message when the API sent one.
 *
 * A page whose chunk is gone (a deploy happened while the tab was open, `staleChunk.ts`) is not
 * recoverable that way: `React.lazy` keeps the failed import, so retrying the route fails again.
 * It lands here only when the automatic reload was refused (`lazyPage`), and the button reloads
 * the page instead. Without a connection the failure says nothing about a new version, so the
 * card only says the screen could not be downloaded and asks to reload once the signal is back.
 * The button stays enabled either way: `navigator.onLine` can say offline on a working connection
 * (some VPNs and virtual adapters), and an installed console has no browser reload to fall back
 * on. The root boundary covers the whole console, menu included, so the card keeps "Volver al
 * inicio": a new location resets the boundary and brings back the screens already loaded.
 */
export function RouteErrorPage({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const online = useOnline();

  if (isChunkLoadError(error)) {
    return (
      <Card withBorder radius="md" padding="xl" maw={520}>
        <Stack gap="sm" align="flex-start">
          <Title order={2}>
            {online ? t('error.newVersionTitle') : t('error.pageOfflineTitle')}
          </Title>
          <Text c="dimmed" size="sm">
            {online ? t('error.newVersionDescription') : t('error.pageOfflineDescription')}
          </Text>
          <Group gap="xs">
            <Button onClick={() => browserPage.reload()}>{t('action.reloadPage')}</Button>
            <Button component={Link} to="/" variant="subtle">
              {t('action.goHome')}
            </Button>
          </Group>
        </Stack>
      </Card>
    );
  }

  const apiError = error instanceof ApiError ? error : null;
  const description =
    apiError?.errorCode === ERROR_CODES.rateLimitExceeded
      ? t('error.rateLimited')
      : t('error.routeDescription');

  const retry = () => {
    reset?.();
    void router.invalidate();
  };

  return (
    <Card withBorder radius="md" padding="xl" maw={520}>
      <Stack gap="sm" align="flex-start">
        <Title order={2}>{t('error.routeTitle')}</Title>
        <Text c="dimmed" size="sm">
          {description}
        </Text>
        {apiError?.correlationId ? (
          <Text c="dimmed" size="xs" className="sl-tabular">
            {t('error.referenceCode', { correlationId: apiError.correlationId })}
          </Text>
        ) : null}
        <Group gap="xs">
          <Button onClick={retry}>{t('action.retry')}</Button>
          <Button component={Link} to="/" variant="subtle">
            {t('action.goHome')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
